/**
 * Testimonials layout variants (NOS-152). `grid` remains the original
 * TestimonialsBlock layout (untouched default). Variants share the block's
 * content fields — switching never loses content — and are token-driven via
 * the --nos-* variables.
 */
import { useState } from "react"
import { HEADING_COLOR, type BlockComponentProps } from "./shared"

interface Testimonial {
  quote?: string
  author?: string
  role?: string
}

function parseTestimonialsContent(content: Record<string, unknown> | null) {
  const heading = (content?.heading as string) ?? "What people say"
  const items = (content?.testimonials as Testimonial[]) ?? []
  return { heading, items }
}

/**
 * Spotlight: one large quote at a time with dot navigation. All testimonials
 * stay in content — only the active one renders.
 */
export function TestimonialsSpotlight({ content }: BlockComponentProps) {
  const { heading, items } = parseTestimonialsContent(content)
  const [active, setActive] = useState(0)
  const current = items[Math.min(active, Math.max(0, items.length - 1))]
  return (
    <section className="px-4 @sm:px-6 @md:px-8" style={{ background: "var(--nos-color-surface, #ffffff)", paddingTop: "var(--nos-space-section-y, 64px)", paddingBottom: "var(--nos-space-section-y, 64px)" }}>
      <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
        <h2 className="text-2xl font-bold" style={{ color: HEADING_COLOR }}>
          {heading}
        </h2>
        <span
          className="mt-6 font-serif text-6xl leading-none"
          style={{ color: "var(--nos-color-primary, #3b82f6)" }}
          aria-hidden
        >
          &ldquo;
        </span>
        {current?.quote && (
          <blockquote className="mt-2 text-2xl leading-relaxed @sm:text-3xl" style={{ color: "var(--nos-color-text, #0f172a)" }}>
            {current.quote}
          </blockquote>
        )}
        {(current?.author || current?.role) && (
          <footer className="mt-6 text-sm" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {current?.author && <span className="font-semibold">{current.author}</span>}
            {current?.author && current?.role && <span aria-hidden> — </span>}
            {current?.role && <cite className="not-italic">{current.role}</cite>}
          </footer>
        )}
        {items.length > 1 && (
          <div className="mt-8 flex items-center gap-2.5">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show testimonial ${i + 1}`}
                aria-pressed={i === active}
                onClick={() => setActive(i)}
                className="h-2.5 w-2.5 rounded-full transition-colors"
                style={{
                  background: i === active ? "var(--nos-color-primary, #3b82f6)" : "var(--nos-color-surface-alt, #f1f5f9)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

/** Wall: CSS-columns masonry of quote cards with alternating backgrounds. */
export function TestimonialsWall({ content }: BlockComponentProps) {
  const { heading, items } = parseTestimonialsContent(content)
  return (
    <section className="px-4 @sm:px-6 @md:px-8" style={{ background: "var(--nos-color-surface, #ffffff)", paddingTop: "var(--nos-space-section-y, 64px)", paddingBottom: "var(--nos-space-section-y, 64px)" }}>
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-8 text-center text-2xl font-bold" style={{ color: HEADING_COLOR }}>
          {heading}
        </h2>
        <div className="columns-1 gap-4 @sm:columns-2 @lg:columns-3">
          {items.map((item, i) => (
            <blockquote
              key={i}
              className={`mb-4 break-inside-avoid p-5${i % 2 === 0 ? " border" : ""}`}
              style={{
                borderRadius: "var(--nos-radius, 12px)",
                background:
                  i % 2 === 0
                    ? "var(--nos-color-surface, #ffffff)"
                    : "color-mix(in srgb, var(--nos-color-primary, #3b82f6) 8%, transparent)",
              }}
            >
              {item.quote && (
                <p className="leading-relaxed" style={{ color: "var(--nos-color-text, #0f172a)" }}>
                  &quot;{item.quote}&quot;
                </p>
              )}
              {item.author && (
                <footer className="mt-3 text-sm font-semibold" style={{ color: "var(--nos-color-primary, #3b82f6)" }}>
                  {item.author}
                </footer>
              )}
              {item.role && (
                <cite className="block text-xs not-italic" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
                  {item.role}
                </cite>
              )}
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  )
}
